import gql from 'graphql-tag';
import React from 'react';

import {onUpdateAntibodyVersion} from 'antibodies/actions/AntibodyActionUtil';
import {resetAntibodyToVersion} from 'antibodies/AntibodyDAO';
import useAntibodyUIState from 'antibodies/hooks/useAntibodyUIState';
import {
  AntibodyHistoryQuery,
  AntibodyHistoryQueryVariables,
} from 'antibodies/panels/__generated/AntibodyHistoryQuery';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BasicFolderItem from 'basic-folder-item/BasicFolderItem';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AntibodyHistoryPanelState} from 'FileStates';
import CloneModal from 'folder-items/CloneModal';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ReactPanelBaseView, {getTabAndPanelIds, Subpanel} from 'toolbar/ReactPanelBaseView';
import HistoryPanelComponent from 'toolbar/shared/HistoryPanelComponent';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';

export const ANTIBODY_HISTORY_QUERY = gql`
  query AntibodyHistoryQuery($antibodyId: StrictString!) @skipAudit @engComponent(name: ANTIBODIES) {
    antibody(antibodyId: $antibodyId) {
      ... on AntibodyNode {
        id
        currentVersionId
        fullHistory {
          id
          name
          creator {
            id
            name
          }
          createdAt
          parent {
            ... on AntibodyVersionNode {
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface AntibodyHistoryPanelProps {
  antibodyId: string;
  antibodyName: string;
  folderId?: string | number;
  permissions?: Permissions;
  setIsLatest: (isLatest: boolean) => void;
}

/**
 * Antibody history panel component. Exported for testing.
 */
export function AntibodyHistoryPanel({
  antibodyId,
  antibodyName,
  folderId,
  permissions,
  setIsLatest,
}: AntibodyHistoryPanelProps): JSX.Element {
  const {
    state: {openVersionId},
    setOpenVersionId,
  } = useAntibodyUIState(antibodyId);

  async function resetToVersion(versionId: string, expectedCurrentVersionId: string): Promise<void> {
    const {versionId: newVersionId, tags} = await resetAntibodyToVersion(
      antibodyId,
      versionId,
      expectedCurrentVersionId
    );
    onUpdateAntibodyVersion(antibodyId, newVersionId, tags);
  }

  function cloneFromVersion(versionId: string): void {
    legacyRunModal(CloneModal, {
      modelType: ModelTypeConstants.BASIC_FOLDER_ITEM,
      itemType: TagSchemaItemType.ANTIBODY,
      id: antibodyId,
      versionId,
      initialName: `${antibodyName} (new)`,
      initialFolderId: folderId,
    });
  }

  if (openVersionId == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <BenchlingQuery<AntibodyHistoryQuery, AntibodyHistoryQueryVariables>
      query={ANTIBODY_HISTORY_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{antibodyId}}
    >
      {({antibody}) => {
        assert(!isInaccessible(antibody), 'Antibody must be accessible.');

        const formattedVersions = antibody.fullHistory.map((version) => ({
          ...version,
          parentId: version.parent?.id ?? null,
          modifiedAt: version.createdAt,
          fetching: false,
        }));

        const openVersion = (versionId: string): void => {
          setOpenVersionId(versionId);
          setIsLatest(antibody.currentVersionId === versionId);
        };

        return (
          <HistoryPanelComponent
            openVersion={openVersion}
            resetToVersion={() => {
              resetToVersion(openVersionId, antibody.currentVersionId);
            }}
            cloneFromVersion={() => {
              cloneFromVersion(openVersionId);
            }}
            versions={formattedVersions}
            openVersionId={openVersionId}
            isLatest={openVersionId === antibody.currentVersionId}
            unsaved={false}
            showNames={true}
            showSeconds={true}
            hasParent={false}
            parent={null}
            permissions={permissions}
          />
        );
      }}
    </BenchlingQuery>
  );
}

class AntibodyHistoryPanelView extends ReactPanelBaseView<AntibodyHistoryPanelState, BasicFolderItem> {
  getSubpanels(): Array<Subpanel> {
    return [
      {
        ...getTabAndPanelIds('history'),
        title: 'History',
        component: (
          <AntibodyHistoryPanel
            antibodyId={this.model.id}
            antibodyName={this.model.name()}
            folderId={this.model.folder()?.id}
            permissions={this.model.permissions()}
            setIsLatest={(isLatest: boolean) => {
              this.model.setIsLatest(isLatest);
            }}
          />
        ),
      },
    ];
  }
}

export default AntibodyHistoryPanelView;
